import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, download } from './client';
import type { FieldErrorDto } from './errors';
import type { FormDefinition } from '../form-engine';
import type { FormType } from './forms';

export interface VersionRef {
  id: string;
  versionNo: number;
  version: number;
  publishedAt: string | null;
  publishedBy: string | null;
  lastModifiedAt: string;
  lastModifiedBy: string;
}

export interface TemplateSummary {
  formType: FormType;
  templateId: string;
  published: VersionRef | null;
  draft: VersionRef | null;
  versionCount: number;
}

export interface TemplateVersion {
  id: string;
  versionNo: number;
  status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
  version: number;
  publishedAt: string | null;
  publishedBy: string | null;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
  definition: FormDefinition | null;
}

export function useTemplates() {
  return useQuery({ queryKey: ['templates'], queryFn: () => api<TemplateSummary[]>('/form-templates') });
}

export function useTemplateVersions(type: FormType | undefined) {
  return useQuery({ queryKey: ['templates', type, 'versions'], queryFn: () => api<TemplateVersion[]>(`/form-templates/${type}/versions`), enabled: Boolean(type) });
}

export function useTemplateVersion(type: FormType | undefined, versionNo: number | undefined) {
  return useQuery({
    queryKey: ['templates', type, 'versions', versionNo],
    queryFn: () => api<TemplateVersion>(`/form-templates/${type}/versions/${versionNo}`),
    enabled: Boolean(type && versionNo),
  });
}

export function usePublished(type: FormType | undefined) {
  return useQuery({ queryKey: ['templates', type, 'published'], queryFn: () => api<TemplateVersion>(`/form-templates/${type}/published`), enabled: Boolean(type) });
}

export function useDraft(type: FormType | undefined) {
  return useQuery({
    queryKey: ['templates', type, 'draft'],
    queryFn: () => api<TemplateVersion>(`/form-templates/${type}/draft`),
    enabled: Boolean(type),
    retry: false,
  });
}

export function useSaveDraft(type: FormType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { definition: FormDefinition; version?: number }) =>
      api<TemplateVersion>(`/form-templates/${type}/draft`, { method: 'PUT', body: input, version: input.version }),
    onSuccess: (draft) => {
      qc.setQueryData(['templates', type, 'draft'], draft);
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

export function useDeleteDraft(type: FormType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => api<void>(`/form-templates/${type}/draft`, { method: 'DELETE', version }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['templates', type, 'draft'] });
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

export function validateDraft(type: FormType, definition?: FormDefinition) {
  return api<{ valid: boolean; errors: FieldErrorDto[] }>(`/form-templates/${type}/draft/validate`, { method: 'POST', body: definition ? { definition } : {} });
}

export function usePublishDraft(type: FormType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => api<TemplateVersion>(`/form-templates/${type}/draft/publish`, { method: 'POST', body: { version }, version }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['templates', type, 'draft'] });
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

/** Largest template file the server accepts (D32). */
export const TEMPLATE_FILE_MAX_BYTES = 1024 * 1024;

/** The version (also the draft) as a JSON file `sablona-{TYPE}-v{n}.json`. */
export function downloadTemplateVersion(type: FormType, versionNo: number) {
  return download(`/form-templates/${type}/versions/${versionNo}/export`, undefined, `sablona-${type}-v${versionNo}.json`);
}

/**
 * Imports the content of a template file into the draft (replaces it when there is one: `version` of the draft is
 * then required). Nothing is published.
 */
export function useImportTemplate(type: FormType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; version?: number }) =>
      api<TemplateVersion>(`/form-templates/${type}/draft/import`, { method: 'POST', json: input.content, version: input.version }),
    onSuccess: (draft) => {
      qc.setQueryData(['templates', type, 'draft'], draft);
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}
